import React, { useEffect, useRef, useState } from 'react'
import '../../message/Message.css'
import { checkShowLogo, defineMessageBorder, messageContainerSize, checkIsLastMessageInCurrentFlow } from './config'
import Typing from '../../message/Typing'
import MessageBody from '../../message/MessageBody'
import CenteredLoader from '../../view/CenteredLoader'
import { isElementTop } from '../../../actions/static'
import { Webview } from '../Webview/Webview'
import classNames from 'classnames'
import classes from './styles.module.scss'
import SelectDateModal from '../../SelectDateModal'
import { initialDatePickerSettings } from '../../../app/config'
import ImageModal from '../../ImageModal/ImageModal'
import { generatePostBackObject } from '../../../actions/static'
import { datadogRum } from '@datadog/browser-rum-slim'

const MessageContainer = props => { //NOSONAR
  const {
    chatId,
    postMessage,
    showTyping,
    isTextDisabled,
    messages,
    parentURL,
    device,
    settings,
    isMobile,
    setIframe,
    isUserIdle,
    isEmpowerPre,
    isEmpowerPost,
    isPanoramaPost,
    iframeSettings,
    getMoreChatHistory,
    isMaximized,
    isScrollChat,
    setAnchorMenu,
    firstUnreadMessageId,
    avatarLogo,
    isRtl,
    setIsIframeContentLoaded,
    setIsImageZoomed,
    setIsLoadingIframeData,
    isLoadingIframeData,
    imageModalSettings,
    setImageModalSettings,
    datePickerSettings,
    setDatePickerSettings,
    updateMessagesState,
    passedOrSkippedFlowComponentsId,
    setPassedOrSkippedFlowComponentsId,
    widgetIframeSource,
    setWidgetIframeSource,
    language,
    startTiming,
    widgetLoadedStartTiming,
    isDDRumTimingSent,
    setIsDDRumTimingSent,
    textMessageRef,
    persistentMenuButtonRef
  } = props

  const [scrollBehavior, setScrollBehavior] = useState('auto')
  const [isImageLoaded, setIsImageLoaded] = useState(false)

  const scrollContainer = useRef(null)
  const messagesEnd = useRef(null)
  const firstUnreadMessageRef = useRef(null)
  const messagesRef = useRef<HTMLElement[]>([])

  const showLoader = !showTyping && messages?.length <= 0
  const containerSize = messageContainerSize(
    device,
    isTextDisabled,
    isMaximized
  )

  const messageBodyRcvdClasses = 'chat__bubble chat__bubble--rcvd'
  const messageBodySentClasses = 'chat__bubble chat__bubble--sent'

  useEffect(() => {
    isUserIdle && setDatePickerSettings(initialDatePickerSettings)
  }, [isUserIdle])

  useEffect(() => {
    setTimeout(scrollToBottom, 750)
  }, [isTextDisabled])

  useEffect(() => {
    scrollToBottom()
  }, [messages?.length, showTyping, isImageLoaded])

  useEffect(() => {
    if (!showLoader && !isDDRumTimingSent) {
      const timing = Date.now() - startTiming
      const widgetLoadedTiming = Date.now() - widgetLoadedStartTiming
      datadogRum.addTiming('first-message-received', timing)
      datadogRum.addTiming('widget-loaded-first-message-received', widgetLoadedTiming)
      setIsDDRumTimingSent(true)
    }
  }, [showLoader, isDDRumTimingSent, startTiming, widgetLoadedStartTiming])

  const onScroll = ({ target }) => {
    if (isElementTop(target) && target === scrollContainer.current) {
      setScrollBehavior('auto')
      getMoreChatHistory()
    }
  }

  const scrollToBottom = () => {
    if (messagesEnd.current) {
      messagesEnd.current.scrollIntoView()
    }
    setScrollBehavior('smooth')
  }

  const selectDateHandler = (formattedDate, displayDate) => {
    const { payload } = datePickerSettings
    const postbackParams = {
      title: displayDate,
      payload: `${payload.split(';?attributes=')[0]};?date=${formattedDate}`
    }
    const postback = generatePostBackObject(postbackParams)
    postMessage(postback)
  }

  return (
    <div
      ref={scrollContainer}
      className={classNames(classes.msgWrapContainer, {
        [`${classes.withoutScroll}`]: !isScrollChat
      })}
      role='dialog'
      aria-modal={true}
      aria-labelledby='widget-header'
      onScroll={onScroll}
      style={containerSize}>
      {showLoader ? (
        <CenteredLoader
          position={'relative'}
          borderTopColor={settings.color}
          size={48}
        />
      ) : (
        messages?.slice()?.map((message, index) => (
          <MessageBody
            key={message.id}
            device={device}
            message={message}
            parentURL={parentURL}
            settings={settings}
            isMobile={isMobile}
            setIframe={setIframe}
            iframeSettings={iframeSettings}
            isEmpowerPre={isEmpowerPre}
            isEmpowerPost={isEmpowerPost}
            isPanoramaPost={isPanoramaPost}
            showReply={index + 1 === messages.length}
            showLogo={checkShowLogo(messages, index)}
            messageBorder={defineMessageBorder(messages, index)}
            postMessage={msg => postMessage(msg)}
            classes={message.isFromBot ? messageBodyRcvdClasses : messageBodySentClasses}
            scrollToBottom={scrollToBottom}
            isMaximized={isMaximized}
            firstUnreadMessageId={firstUnreadMessageId}
            firstUnreadMessageRef={firstUnreadMessageRef}
            avatarLogo={avatarLogo}
            setDatePickerSettings={setDatePickerSettings}
            isImageLoaded={isImageLoaded}
            setIsImageLoaded={setIsImageLoaded}
            setIsImageZoomed={setIsImageZoomed}
            isLastMessageInCurrentFlow={checkIsLastMessageInCurrentFlow(messages, index)}
            setImageModalSettings={setImageModalSettings}
            isFirstMessageInCurrentFlow={index === 0}
            isLoadingIframeData={isLoadingIframeData}
            messagesRef={messagesRef}
            messageIndex={index}
            updateMessagesState={updateMessagesState}
            passedOrSkippedFlowComponentsId={passedOrSkippedFlowComponentsId}
            setPassedOrSkippedFlowComponentsId={setPassedOrSkippedFlowComponentsId}
            textMessageRef={textMessageRef}
          />
        ))
      )}

      {iframeSettings.isIframeOpen && iframeSettings.iframeSrc && (
        <Webview
          chatId={chatId}
          device={device}
          settings={settings}
          isMobile={isMobile}
          isPanoramaPost={isPanoramaPost}
          setIframe={setIframe}
          isUserIdle={isUserIdle}
          iframeSettings={iframeSettings}
          setAnchorMenu={setAnchorMenu}
          isMaximized={isMaximized}
          setIsIframeContentLoaded={setIsIframeContentLoaded}
          setIsImageZoomed={setIsImageZoomed}
          setIsLoadingIframeData={setIsLoadingIframeData}
          isLoadingIframeData={isLoadingIframeData}
          persistentMenuButtonRef={persistentMenuButtonRef}
          widgetIframeSource={widgetIframeSource}
          setWidgetIframeSource={setWidgetIframeSource}
          language={language}
        />
      )}

      {datePickerSettings.isShowSelectDate &&
        <SelectDateModal
          onChange={selectDateHandler}
          datePickerSettings={datePickerSettings}
          setDatePickerSettings={setDatePickerSettings}
          isLoadingIframeData={isLoadingIframeData}
          language={language}
        />
      }

      {imageModalSettings.imageModalSrc &&
        <ImageModal
          imageUrl={imageModalSettings.imageModalSrc}
          imageAltText={imageModalSettings.imageAltText}
          setImageModalSettings={setImageModalSettings}
          settings={settings}
        />
      }

      {showTyping && <Typing />}

      <div className={classes.bottomScrollElem} ref={messagesEnd} />
    </div>
  )
}

export default MessageContainer
